/**
 * CR-MerchantName: Single canonical component for rendering merchant name
 * 
 * This component renders the merchant name with appropriate styling based on:
 * - placement (where it appears in the card)
 * - style (TITLE, SUBTITLE, SMALL)
 * - color context (header vs body)
 */

import { cn } from "@/lib/utils";
import type { MerchantNameStyle } from "@/lib/deals/globalThemeTokens";

interface MerchantNameBlockProps {
  /** The merchant name to display */
  merchantName: string;
  /** Text style preset */
  style: MerchantNameStyle;
  /** Whether rendering in header (light text) or body (dark text) */
  context: "header" | "body";
  /** Optional custom text color (HSL CSS value) */
  textColor?: string;
  /** Additional className */
  className?: string;
}

/**
 * Get Tailwind classes based on style preset
 */
function getStyleClasses(style: MerchantNameStyle, context: "header" | "body"): string {
  const baseColor = context === "header" 
    ? "text-white" 
    : "text-foreground";
  
  switch (style) {
    case "TITLE":
      return cn("text-lg md:text-xl font-semibold", baseColor);
    case "SUBTITLE":
      return cn("text-base md:text-lg font-medium", baseColor);
    case "SMALL":
      return cn("text-sm font-medium", context === "header" ? "text-white/90" : "text-foreground/80");
    default:
      return cn("text-lg md:text-xl font-semibold", baseColor);
  }
}

export function MerchantNameBlock({
  merchantName,
  style,
  context,
  textColor,
  className,
}: MerchantNameBlockProps) {
  if (!merchantName) return null;

  const styleClasses = getStyleClasses(style, context);

  return (
    <span
      className={cn(
        styleClasses,
        "truncate max-w-[200px] block",
        className
      )}
      style={textColor ? { color: textColor } : undefined}
    >
      {merchantName}
    </span>
  );
}
